import React, { useState, useContext, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import CashierApi from "../../utility/CashierApi";

import { CustomerContext } from "../CashierHomepage";
import { CustomerIdContext } from "../../context/CustomerIdProvider";
import { AutoBillContext } from "../../context/AutoBillProvider";

import DeleteCartItem from "./DeleteCartItem";
import CartItemQuantity from "./CartItemQuantity";
import CartAutobill from "./CartAutobill";
import { getOfflineCart } from "../../utility/offline/offlineCart";
import { isOffline } from "../../utility/offline/reachability";
import { flushOfflineCart } from "../../utility/offline/saleQueue";

function CartItem({ setTotalSum, currency }) {
  const [subtotal, setSubtotal] = useState(0);
  const { fetchOnlyData } = CashierApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { autoBill, setAutoBill } = useContext(AutoBillContext);
  const { selectedCustomerId } = useContext(CustomerIdContext);
  const { setNewCartData } = useContext(CustomerContext);

  // Fetch cart data
  const { data } = useQuery({
    queryKey: ["cart", selectedCustomerId],
    queryFn: async () => {
      // An offline cart is the truth until it has been pushed to the server.
      if (getOfflineCart(selectedCustomerId)) {
        if (isOffline()) return getOfflineCart(selectedCustomerId).items;
        try {
          await flushOfflineCart(selectedCustomerId);
        } catch {
          return getOfflineCart(selectedCustomerId).items;
        }
      }
      try {
        return await fetchOnlyData(`carts/${selectedCustomerId}`);
      } catch (error) {
        if (!error?.isNetworkError) throw error;
        return (
          getOfflineCart(selectedCustomerId)?.items ??
          queryClient.getQueryData(["cart", selectedCustomerId]) ??
          []
        );
      }
    },
    enabled: !!selectedCustomerId,
  });

  // Calculate subtotal (sum of all cart items by price * quantity)
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) {
      setSubtotal(0);
      setTotalSum(0);
      setNewCartData && setNewCartData(data);
      return;
    }
    const sub = data.reduce(
      (sum, item) =>
        sum + (Number(item?.price) * Number(item?.quantity)),
      0
    );
    setSubtotal(sub);
    setNewCartData && setNewCartData(data);
  }, [data, setNewCartData, setTotalSum]);

  // Calculate discount and tax amounts from autoBill
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) {
      setTotalSum(0);
      return;
    }

    let discountAmount = 0;
    let taxAmount = 0;

    if (Array.isArray(autoBill)) {
      autoBill.forEach((bill) => {
        const billName = bill?.bill_name?.toLowerCase() || "";
        const billAmount = Number(bill?.auto_bill) || 0;
        if (billName.includes("discount")) {
          discountAmount += billAmount;
        } else if (billName.includes("vat") || billName.includes("tax")) {
          taxAmount += billAmount;
        }
      });
    }

    // Calculate total: subtotal - discount + tax
    const total = subtotal - discountAmount + taxAmount;
    setTotalSum(Number(total));
  }, [subtotal, autoBill, data, setTotalSum]);

  // Handle unauthorized access
  if (
    typeof data === "object" &&
    (data?.status === 401 || data?.response?.status === 401)
  ) {
    navigate("/login");
  }

  return (
    <tbody>
      {data?.map((item, index) => (
        <tr
          key={item.id}
          className="hover:bg-gray-950 border-b border-gray-300 h-4"
        >
          <td className="py-2 px-1 text-center">{index + 1}</td>
          <td className="py-2 px-2 text-center">
            {item?.product?.product_name}
          </td>
          <td className="py-2 px-2 text-center">
            {currency}
            {item?.price}
          </td>
          <td>
            <CartItemQuantity item={item} />
          </td>
          <td>
            <DeleteCartItem itemId={item.id} data={data} />
          </td>
        </tr>
      ))}
      {data?.length > 0 && (
        <CartAutobill
          currency={currency}
          total={subtotal}
          autoBill={autoBill}
          setAutoBill={setAutoBill}
        />
      )}
    </tbody>
  );
}

export default React.memo(CartItem);
